import { Streamfield, WagtailArticleAuthor, WagtailImage } from '@/types';
import { addBaseUrl } from '@/utils/url';
import ArticleMetaData from '@components/ArticleMetaData';
import ArticleSidebar from '@components/ArticleSidebar';
import ArticleTags from '@components/ArticleTags';
import Streamfields from '@streamfields/Streamfields';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { BasePageProps, basePageWrap } from '../BasePage';

const useMediaQuery = (width: number) => {
  const [targetReached, setTargetReached] = useState(false);

  const updateTarget = useCallback((e: MediaQueryListEvent) => {
    if (e.matches) {
      setTargetReached(true);
    } else {
      setTargetReached(false);
    }
  }, []);

  useEffect(() => {
    const media = window.matchMedia(`(max-width: ${width}px)`);
    media.addEventListener('change', updateTarget);

    // Check on mount (callback is not called until a change occurs)
    if (media.matches) {
      setTargetReached(true);
    }

    return () => media.removeEventListener('change', updateTarget);
  }, [updateTarget, width]);

  return targetReached;
};

type Props = {
  title: string;
  subtitle: string;
  primary_image?: WagtailImage;
  content: Streamfield[];
  authors: WagtailArticleAuthor[];
  tags: string[];
  pub_date: string;
  reading_time: Number;
} & BasePageProps;

function ArticlePage(props: Props) {
  const isMobil = useMediaQuery(1040);
  const { title, primary_image, content, authors, tags, pub_date, reading_time, subtitle } = props;

  return (
    <div className='article-page'>
      <div className='container title_container'>
        <h1 className='page-title'>{title}</h1>
        {subtitle ? <div className='subtitle'>{subtitle}</div> : null}
      </div>
      <ArticleMetaData date={pub_date} readingTime={reading_time} />
      <div className='article-page_content_container'>
        <ArticleSidebar mobile={isMobil} authors={authors} tags={tags} />

        <div className='article-page_copy'>
          {primary_image ? (
            <Image
              className='primary-image'
              src={addBaseUrl(primary_image.meta.download_url)}
              alt={primary_image.title}
              width={900}
              height={900}
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          ) : null}
          {content ? <Streamfields fields={content} /> : null}

          {isMobil && <ArticleTags tags={tags} />}
        </div>
      </div>
    </div>
  );
}

export default basePageWrap<Props>(ArticlePage);
